(ns manage.ad-variant.creation.manual-upload-ocp.carousel
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["react-hook-form" :as rhf]
            ["src/svg/inform/plus.svg$default" :as Plus]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.db.asset-upload.resumable :as asset-upload.resumable]
            [common.form :as form]
            [common.url-template :as url-tpl]
            [common.widgets.form-field :refer [form-field hint]]
            [common.widgets.generic-upload :as generic-upload]
            [manage.ad-variant.creation.manual-upload-ocp.hooks :as hooks]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def ^:private media-kind-options
  [{:value "image" :label "Image"}
   {:value "video" :label "Video"}])

(def ^:private default-card
  #js {:media #js {:kind "image" :urls #js []}
       :headline ""
       :link ""})

(defhook use-carousel-cards [field-name set-current-idx]
  (let [{:keys [fields append remove]} (form/use-field-array #js {:name field-name})
        {errors :errors submit-count :submitCount} (->clj (rhf/useFormState))
        initial-added (uix/use-ref false)]
    (uix/use-effect
     (fn []
       (when-not @initial-added
         (reset! initial-added true)
         (append default-card)
         (append default-card)))
     [append])
    (uix/use-effect
     (fn navigate-to-first-error []
       (when (pos? submit-count)
         (when-first [first-err (->> (get-in errors [:components :cards])
                                     (keep (fn [[k v]] (when (seq v) (js/parseInt (name k)))))
                                     sort)]
           (set-current-idx first-err))))
     [errors set-current-idx submit-count])
    {:fields fields
     :add-card (fn []
                 (append default-card)
                 (set-current-idx (count fields)))
     :on-delete (fn [del-idx]
                  (remove del-idx)
                  (set-current-idx #(if (and (pos? %) (>= % del-idx)) (dec %) %)))}))

(def ^:private image-extensions ".jpg, .jpeg, .png, .gif")
(def ^:private image-mimes "image/jpeg,image/png,image/gif")
(def ^:private video-extensions ".mp4, .mov")
(def ^:private video-mimes "video/mp4,video/quicktime")
(def ^:private thumbnail-extensions ".jpg, .jpeg, .png")
(def ^:private thumbnail-mimes "image/jpeg,image/png")

(defui image-row [{:keys [label secondary state on-remove icon sx]
                   :or {icon "insert_drive_file"}}]
  ($ Stack {:direction "row" :alignItems "center" :gap 1
            :sx (js/Object.assign #js {:px 1.5 :py 0.5} (or sx #js {}))}
     (case state
       :pending ($ CircularProgress {:size 16})
       :failure ($ Icon {:color "error" :sx #js {:fontSize 18}} "error")
       ($ Icon {:sx #js {:fontSize 18}} icon))
     ($ Typography {:variant "body2" :noWrap true :sx #js {:flex 1}}
        label)
     (when secondary
       ($ Typography {:variant "body2" :color "text.secondary"}
          secondary))
     (when on-remove
       ($ IconButton {:size "small" :onClick on-remove}
          ($ Icon {:sx #js {:fontSize 16}} "close")))))

(defui pending-image-row [{:keys [path file-name on-dismiss sx]}]
  (let [{:keys [state]} (use-subscribe [:asset-upload.resumable/progress path])
        failed? (= :failure state)]
    ($ image-row {:label file-name
                  :sx sx
                  :state (if failed? :failure :pending)
                  :secondary (when failed? "Upload failed")
                  :on-remove (when failed? on-dismiss)})))

(defn- read-image-dimensions
  "The pixel width/height of an image file, read locally in the browser before
   upload. The preview needs these to match a card's several media to the aspect
   ratio they were customized for, the same way the backend does once uploaded."
  [file cb]
  (let [img (js/Image.)
        object-url (.createObjectURL js/URL file)
        cleanup #(.revokeObjectURL js/URL object-url)]
    (set! (.-onload img)
          (fn []
            (cb {:width (.-naturalWidth img) :height (.-naturalHeight img)})
            (cleanup)))
    (set! (.-onerror img)
          (fn []
            (cb nil)
            (cleanup)))
    (set! (.-src img) object-url)))

(defui carousel-card-images
  "Upload for the images of a card. One image is a regular carousel card, one image
   per placement format customizes the carousel per Meta placement."
  [{:keys [name field-id]}]
  (let [url-context (use-subscribe [:url-context])
        [pending set-pending] (uix/use-state {})
        ;; the url of an upload arrives via callback, the ref keeps concurrent uploads
        ;; from overwriting each other and remembers file names for the rows
        urls-ref (uix/use-ref [])
        file-meta (uix/use-ref {})]
    ($ rhf/Controller
       {:name (str name ".media.urls")
        :render
        (fn [^js props]
          (let [{{:keys [error]} :fieldState
                 {:keys [onChange onBlur value]} :field} (->clj props)
                urls (vec (->clj (or value [])))
                _ (reset! urls-ref urls)
                set-urls (fn [next]
                           (reset! urls-ref (vec next))
                           (onChange (->js @urls-ref))
                           (onBlur))
                on-drop (fn [files]
                          (doseq [file files]
                            (let [id (str (random-uuid))
                                  path [::carousel-card-image field-id id]
                                  upload! (fn [dimensions]
                                            (asset-upload.resumable/start-upload
                                             {:uri (url-tpl/render :api/asset-upload.resumable url-context)
                                              :path path
                                              :file file
                                              :on-change (fn [url]
                                                           (swap! file-meta assoc url {:file-name (.-name file)
                                                                                       :size (.-size file)})
                                                           (set-urls (conj @urls-ref (merge {:url url} dimensions)))
                                                           (set-pending #(dissoc % id)))}))]
                              (set-pending #(assoc % id {:file-name (.-name file) :path path}))
                              (read-image-dimensions file upload!))))]
            ($ form-field {:label "Images" :err-msg (:message error)}
               ($ generic-upload/upload
                  {:class [(when error "invalid")]
                   :on-drop on-drop
                   :on-drop-rejected #(rf/dispatch [:toaster-bus/add {:type :error
                                                                      :message "Invalid filesize or type"}])
                   :accept image-mimes
                   :extensions image-extensions
                   :multiple true
                   :relative-box true
                   :preview (when (or (seq urls) (seq pending))
                              ($ Stack {:divider ($ Divider)}
                                 (for [[idx {:keys [url width height]}] (mc/seq-indexed urls)
                                       :let [{:keys [file-name size]} (get @file-meta url)]]
                                   ($ image-row
                                      {:key (str idx ":" url)
                                       :icon "photo"
                                       :label (or file-name (last (str/split url "/")))
                                       :secondary (not-empty
                                                   (str/join " • "
                                                             (keep identity
                                                                   [(when size (str (Math/round (/ size 1000)) " kB"))
                                                                    (when (and width height) (str width "x" height))])))
                                       :on-remove #(set-urls (concat (subvec urls 0 idx)
                                                                     (subvec urls (inc idx))))}))
                                 (for [[id {:keys [file-name path]}] pending]
                                   ($ pending-image-row
                                      {:key id
                                       :path path
                                       :file-name file-name
                                       :on-dismiss (fn []
                                                     (rf/dispatch [:asset-upload.resumable/destroy path])
                                                     (set-pending #(dissoc % id)))}))))})
               ($ hint (str "Supported formats: " image-extensions
                            ". Add one image per placement format to customize the carousel per placement.")))))})))

(defui carousel-card-video-item
  [{:keys [videos-name idx field-id file-meta on-remove]}]
  (let [thumb-name (str videos-name "[" idx "].thumbnail.url")
        url-context (use-subscribe [:url-context])
        [thumb-pending set-thumb-pending!] (uix/use-state nil)
        [thumb-meta set-thumb-meta!] (uix/use-state nil)
        video-url (rhf/useWatch #js {:name (str videos-name "[" idx "].url")})
        thumb-value (rhf/useWatch #js {:name thumb-name})
        [thumb-visible? set-thumb-visible!] (uix/use-state (not (str/blank? thumb-value)))]

    ($ Stack {:gap 0.5}
       ($ Stack {:direction "row" :justifyContent "space-between" :alignItems "center"}
          ($ Typography {:variant "body2" :fontWeight "medium"}
             (str "Video " (inc idx)))
          ($ IconButton {:onClick on-remove :size "small"}
             ($ Icon {:sx #js {:fontSize 18}} "delete")))
       ($ image-row
          {:label (or (get-in @file-meta [video-url :file-name])
                      (last (str/split (or video-url "") "/")))
           :sx #js {:bgcolor "grey.80" :borderRadius "4px"}})
       ($ rhf/Controller
          {:name thumb-name
           :render
           (fn [^js ctrl]
             (let [{{:keys [error]} :fieldState
                    {:keys [onChange onBlur value]} :field} (->clj ctrl)
                   thumb-url (when-not (str/blank? (or value "")) value)]
               (cond
                 thumb-pending
                 ($ pending-image-row
                    {:path (:path thumb-pending)
                     :file-name (:file-name thumb-pending)
                     :sx #js {:bgcolor "grey.80" :borderRadius "4px"}
                     :on-dismiss #(do (rf/dispatch [:asset-upload.resumable/destroy (:path thumb-pending)])
                                      (set-thumb-pending! nil))})
                 thumb-url
                 ($ image-row
                    {:label (or (:file-name thumb-meta) (last (str/split thumb-url "/")))
                     :sx #js {:bgcolor "grey.80" :borderRadius "4px"}
                     :on-remove #(do (onChange "") (onBlur) (set-thumb-meta! nil))})
                 thumb-visible?
                 ($ form-field {:err-msg (:message error)}
                    ($ generic-upload/upload
                       {:on-drop (fn [files]
                                   (when-let [file (first files)]
                                     (let [p [::carousel-card-thumbnail field-id]]
                                       (set-thumb-pending! {:file-name (.-name file) :path p})
                                       (asset-upload.resumable/start-upload
                                        {:uri (url-tpl/render :api/asset-upload.resumable url-context)
                                         :path p
                                         :file file
                                         :on-change (fn [url]
                                                      (set-thumb-meta! {:file-name (.-name file)})
                                                      (onChange url)
                                                      (onBlur)
                                                      (set-thumb-pending! nil))}))))
                        :on-drop-rejected #(rf/dispatch [:toaster-bus/add {:type :error :message "Invalid filesize or type"}])
                        :accept thumbnail-mimes
                        :extensions thumbnail-extensions}))
                 :else
                 ($ Button
                    {:onClick #(set-thumb-visible! true)
                     :size "small"
                     :variant "text"
                     :startIcon ($ Plus)
                     :sx #js {:alignSelf "flex-start"}}
                    "Add thumbnail (optional)"))))}))))

(defui carousel-card-videos
  "Uploads for the videos of a card. One video is a regular carousel card, one video
   per placement format customizes the carousel per Meta placement. Every video can
   carry its own optional thumbnail."
  [{:keys [name]}]
  (let [videos-name (str name ".media.videos")
        {:keys [fields append remove]} (form/use-field-array #js {:name videos-name})
        url-context (use-subscribe [:url-context])
        upload-id (uix/use-memo #(str (random-uuid)) [])
        [pending set-pending!] (uix/use-state {})
        file-meta (uix/use-ref {})
        [thumb-pending set-thumb-pending!] (uix/use-state nil)
        [thumb-url set-thumb-url!] (uix/use-state nil)
        thumb-url-ref (uix/use-ref nil)
        [thumb-visible? set-thumb-visible!] (uix/use-state false)]
    ($ Stack {:gap 1}
       ($ generic-upload/upload
          {:on-drop (fn [files]
                      (doseq [file files]
                        (let [id (str (random-uuid))
                              path [::carousel-card-video id]]
                          (set-pending! #(assoc % id {:file-name (.-name file) :path path}))
                          (asset-upload.resumable/start-upload
                           {:uri (url-tpl/render :api/asset-upload.resumable url-context)
                            :path path
                            :file file
                            :on-change (fn [url]
                                         (swap! file-meta assoc url {:file-name (.-name file)})
                                         (append #js {:url url
                                                      :thumbnail (when @thumb-url-ref #js {:url @thumb-url-ref})})
                                         (reset! thumb-url-ref nil)
                                         (set-thumb-url! nil)
                                         (set-thumb-visible! false)
                                         (set-pending! #(dissoc % id)))}))))
           :on-drop-rejected #(rf/dispatch [:toaster-bus/add {:type :error :message "Invalid filesize or type"}])
           :accept video-mimes
           :extensions video-extensions})
       (cond
         thumb-pending
         ($ pending-image-row
            {:path (:path thumb-pending)
             :file-name (:file-name thumb-pending)
             :sx #js {:bgcolor "grey.70" :borderRadius "4px"}
             :on-dismiss #(do (rf/dispatch [:asset-upload.resumable/destroy (:path thumb-pending)])
                              (reset! thumb-url-ref nil)
                              (set-thumb-pending! nil))})
         thumb-url
         ($ image-row
            {:label (last (str/split thumb-url "/"))
             :sx #js {:bgcolor "grey.70" :borderRadius "4px"}
             :on-remove #(do (reset! thumb-url-ref nil) (set-thumb-url! nil))})
         thumb-visible?
         ($ generic-upload/upload
            {:on-drop (fn [files]
                        (when-let [file (first files)]
                          (let [p [::carousel-card-thumbnail-new upload-id]]
                            (set-thumb-pending! {:file-name (.-name file) :path p})
                            (asset-upload.resumable/start-upload
                             {:uri (url-tpl/render :api/asset-upload.resumable url-context)
                              :path p
                              :file file
                              :on-change (fn [url]
                                           (reset! thumb-url-ref url)
                                           (set-thumb-url! url)
                                           (set-thumb-pending! nil))}))))
             :on-drop-rejected #(rf/dispatch [:toaster-bus/add {:type :error :message "Invalid filesize or type"}])
             :accept thumbnail-mimes
             :extensions thumbnail-extensions})
         :else
         ($ Button
            {:onClick #(set-thumb-visible! true)
             :size "small"
             :variant "text"
             :startIcon ($ Plus)
             :sx #js {:alignSelf "flex-start"}}
            "Add thumbnail (optional)"))
       (for [[id {:keys [file-name path]}] pending]
         ($ pending-image-row
            {:key id
             :path path
             :file-name file-name
             :sx #js {:bgcolor "grey.70" :borderRadius "4px"}
             :on-dismiss (fn []
                           (rf/dispatch [:asset-upload.resumable/destroy path])
                           (set-pending! #(dissoc % id)))}))
       (for [[idx field] (mc/seq-indexed fields)]
         ($ carousel-card-video-item
            {:key (.-id field)
             :videos-name videos-name
             :idx idx
             :field-id (.-id field)
             :file-meta file-meta
             :on-remove #(remove idx)})))))

(defui carousel-card [{:keys [name format field-id]}]
  (let [cta-options (hooks/use-cta-options format)
        media-kind (or (rhf/useWatch #js {:name (str name ".media.kind")})
                       "image")]
    ($ :<>
       ($ form-field {:label "Media type"}
          ($ form/autocomplete-field
             {:name (str name ".media.kind")
              :field-props {:options media-kind-options}}))
       (if (= "video" media-kind)
         ($ carousel-card-videos {:name name})
         ($ carousel-card-images {:name name :field-id field-id}))
       ($ form-field {:label "Headline (up to 40 characters recommended)"}
          ($ form/form-text-field
             {:name (str name ".headline")}))
       ($ form-field {:label "Description (optional, up to 20 characters recommended)"}
          ($ form/form-text-field
             {:name (str name ".description")}))
       ($ form-field {:label "Link"}
          ($ form/form-text-field
             {:name (str name ".link")
              :field-props {:placeholder "https://"}}))
       (when (seq cta-options)
         ($ form-field {:label "CTA (optional)"}
            ($ form/autocomplete-field
               {:name (str name ".cta")
                :field-props {:options cta-options
                              :clearable? true}}))))))

(defui carousel-cards-component [{:keys [name format card-index on-card-navigate]}]
  (let [{:keys [fields add-card on-delete]}
        (use-carousel-cards name on-card-navigate)
        current-idx (or card-index 0)
        cards (->clj (rhf/useWatch #js {:name name}))
        multi-placement? (boolean (some #(< 1 (hooks/carousel-card-media-count %)) cards))]
    ($ Stack {:gap 2}
       ($ Divider)
       ($ Stack {:direction "row" :gap 1 :flexWrap "wrap" :alignItems "center"}
          (for [[idx field] (mc/seq-indexed fields)]
            ($ Chip
               {:key (.-id field)
                :variant "outlined"
                :size "medium"
                :label (str "Card " (inc idx))
                :color (if (= idx current-idx) "primary" "grey")
                :onClick #(on-card-navigate idx)
                :onDelete (when (> (count fields) 2) #(on-delete idx))
                :deleteIcon ($ Icon "close")
                :sx #js {:borderRadius "16px"
                         ".MuiChip-deleteIcon" #js {:fontSize "16px"}}}))
          ($ Button
             {:onClick add-card
              :disabled (>= (count fields) 10)
              :startIcon ($ Plus)
              :sx #js {:alignSelf "flex-start"}}
             "Add card"))
       (when multi-placement?
         ($ Stack {:direction "row" :alignItems "center" :gap 1}
            ($ Chip {:label "Multi-placement carousel"
                     :color "primary"
                     :size "small"
                     :variant "outlined"
                     :icon ($ Icon {:sx #js {:fontSize 16}} "dashboard")})
            ($ hint "Cards carry several media, the carousel serves the one that fits each Meta placement")))
       (when-let [field (nth fields current-idx nil)]
         ($ carousel-card
            {:key (.-id field)
             :field-id (.-id field)
             :name (str name "[" current-idx "]")
             :format format})))))
